import { useState, useEffect, useCallback } from "react";
import { Info, X } from "lucide-react";

interface ErrorPopupProps {
  error: string | null;
  duration?: number;
  onClose?: () => void;
}

export const ErrorPopup: React.FC<ErrorPopupProps> = ({
  error,
  duration = 5000,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    if (onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    if (error) {
      setIsVisible(true);

      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [error, duration, handleClose]);

  if (!isVisible || !error) return null;

  // Produce a friendly, readable message from the raw error string
  const friendlyMessage = (() => {
    const msg = error.toLowerCase();
    if (
      msg.includes("network") ||
      msg.includes("fetch") ||
      msg.includes("failed to fetch")
    )
      return "We couldn't reach the server. Please check your internet connection and try again.";
    if (msg.includes("unauthorized") || msg.includes("401"))
      return "Your session has expired. Please log in again to continue.";
    if (msg.includes("forbidden") || msg.includes("403"))
      return "You don't have permission to perform this action.";
    if (msg.includes("not found") || msg.includes("404"))
      return "The requested resource could not be found.";
    if (msg.includes("timeout") || msg.includes("timed out"))
      return "The request took too long. Please try again in a moment.";
    if (msg.includes("500") || msg.includes("internal server"))
      return "Something went wrong on our end. Please try again later.";
    return error;
  })();

  return (
    <div className="fixed inset-0 z-[1000000]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-40 transition-opacity pointer-events-auto" />
      {/* Popup container */}
      <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
        <div
          className="pointer-events-auto bg-white border border-red-100 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
          style={{ animation: "slideUp 0.35s cubic-bezier(0.34,1.56,0.64,1)" }}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the popup
        >
          {/* Top accent bar */}
          {/* <div className="h-1 w-full bg-gradient-to-r from-orange-400 to-red-500" /> */}

          <div className="p-5">
            {/* Header row */}
            <div className="flex gap-3 items-center">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                <Info className="w-5 h-5 text-blue-500" />
              </div>

              <div className="flex-1 min-w-0">
                {/* <p className="text-sm font-semibold text-gray-800 leading-tight">
                  Something went wrong
                </p> */}
                <p className="mt-1 text-sm text-gray-500 leading-snug break-words">
                  {friendlyMessage}
                </p>
              </div>

              <button
                onClick={handleClose}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors ml-1 mt-0.5"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Progress bar */}
            <div className="mt-4 w-full bg-gray-100 rounded-full h-1 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full"
                style={{ animation: `shrink ${duration}ms linear forwards` }}
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes shrink {
          from { width: 100%; }
          to   { width: 0%;   }
        }
      `}</style>
    </div>
  );
};
